import type {
  MockCache,
  ChannelId,
  MessageId,
  EpochMs,
  Message,
  UserId,
  ReactionId,
  CommentId,
} from '../../types';
import { normalizeEmoji } from '../../helpers';

import { fanoutLast } from '../fanout';
import { newId } from '../ids';
import { addToIndex, removeFromIndex } from '../indexes';
import type { RichDoc } from '../../rich-doc';
import { richDocFrom, richDocToText, richParagraph } from '../../rich-doc';
import { refreshMentions, snapshot, taskKeys } from '../internal';
import { logActivity } from '../journal';
import { bump, requireCache } from '../runtime';

function touchOwnRead(cache: MockCache, channelId: ChannelId, seq: number): void {
  const key = `${String(channelId)}:${String(cache.me)}`;
  const member = cache.members.get(key);
  if (member !== undefined && member.lastReadSeq < seq) {
    member.lastReadSeq = seq;
  }
}

export function insertMessageRow(
  cache: MockCache,
  row: {
    id: MessageId;
    channelId: ChannelId;
    threadRootId: MessageId | null;
    replyToId: MessageId | null;
    bodyJson: RichDoc;
    sendAt: EpochMs | null;
    systemKind: Message['systemKind'];
    authorId: UserId;
  },
): Message {
  const channel = cache.channels.get(row.channelId);
  if (channel === undefined) {
    throw new Error('Канал не найден');
  }
  const seq = channel.nextSeq;
  channel.nextSeq += 1;
  const now = cache.nowMs();
  const bodyJson = richDocFrom(row.bodyJson);
  const message: Message = {
    id: row.id,
    channelId: row.channelId,
    seq,
    authorId: row.authorId,
    bodyJson,
    bodyText: richDocToText(bodyJson),
    threadRootId: row.threadRootId,
    replyToId: row.replyToId,
    sendAt: row.sendAt,
    published: row.sendAt === null,
    pinned: false,
    editedAt: null,
    deletedAt: null,
    createdAt: now,
    systemKind: row.systemKind,
  };
  cache.messages.set(message.id, message);
  addToIndex(cache, 'message', String(message.id));
  channel.lastMessageAt = now;
  channel.lastMessageSeq = seq;
  touchOwnRead(cache, row.channelId, seq);
  refreshMentions(cache, 'message', String(message.id), message.bodyText);
  return message;
}

export function insertSystemMessage(cache: MockCache, channelId: ChannelId, kind: NonNullable<Message['systemKind']>, text: string): void {
  insertMessageRow(cache, {
    id: newId('message') as MessageId,
    channelId,
    threadRootId: null,
    replyToId: null,
    bodyJson: richParagraph(text),
    sendAt: null,
    systemKind: kind,
    authorId: cache.me,
  });
}

export function sendMessage(args: {
  id: MessageId;
  channelId: ChannelId;
  bodyJson: RichDoc;
  threadRootId?: MessageId | null;
  replyToId?: MessageId | null;
  sendAt?: EpochMs | null;
}): Message {
  const cache = requireCache();
  const threadRootId = args.threadRootId ?? null;
  if (threadRootId !== null) {
    const root = cache.messages.get(threadRootId);
    if (root === undefined || root.threadRootId !== null || root.channelId !== args.channelId) {
      throw new Error('Корень треда не найден: вложенных тредов нет');
    }
  }
  const message = insertMessageRow(cache, {
    id: args.id,
    channelId: args.channelId,
    threadRootId,
    replyToId: args.replyToId ?? null,
    bodyJson: args.bodyJson,
    sendAt: args.sendAt ?? null,
    systemKind: null,
    authorId: cache.me,
  });
  logActivity(cache, {
    mutator: 'message.send',
    entityTable: 'message',
    entityId: String(message.id),
    op: 'insert',
    before: null,
    after: snapshot(message),
  });
  fanoutLast(cache);
  const keys = [`channel:${String(args.channelId)}`, 'inbox'];
  if (threadRootId !== null) {
    keys.push(`thread:${String(threadRootId)}`);
  }
  bump(...keys);
  return message;
}

export function replyInThread(args: { id: MessageId; rootMessageId: MessageId; bodyJson: RichDoc }): Message {
  const cache = requireCache();
  const root = cache.messages.get(args.rootMessageId);
  if (root === undefined || root.threadRootId !== null) {
    throw new Error('Корень треда не найден: вложенных тредов нет');
  }
  const message = insertMessageRow(cache, {
    id: args.id,
    channelId: root.channelId,
    threadRootId: root.id,
    replyToId: null,
    bodyJson: args.bodyJson,
    sendAt: null,
    systemKind: null,
    authorId: cache.me,
  });
  const now = cache.nowMs();
  const thread = cache.threads.get(root.id);
  if (thread === undefined) {
    cache.threads.set(root.id, {
      rootMessageId: root.id,
      channelId: root.channelId,
      replyCount: 1,
      participantIds: [message.authorId],
      lastReplyAt: now,
      followerIds: root.authorId === message.authorId ? [message.authorId] : [root.authorId, message.authorId],
    });
  } else {
    thread.replyCount += 1;
    thread.lastReplyAt = now;
    if (!thread.participantIds.includes(message.authorId)) {
      thread.participantIds.push(message.authorId);
    }
    if (!thread.followerIds.includes(message.authorId)) {
      thread.followerIds.push(message.authorId);
    }
  }
  logActivity(cache, {
    mutator: 'message.reply',
    entityTable: 'message',
    entityId: String(message.id),
    op: 'insert',
    before: null,
    after: snapshot(message),
  });
  fanoutLast(cache);
  bump(`channel:${String(root.channelId)}`, `thread:${String(root.id)}`, 'inbox');
  return message;
}

export function editMessage(args: { id: MessageId; bodyJson: RichDoc }): void {
  const cache = requireCache();
  const message = cache.messages.get(args.id);
  if (message === undefined) {
    throw new Error('Сообщение не найдено');
  }
  const before = { bodyJson: message.bodyJson, bodyText: message.bodyText, editedAt: message.editedAt };
  message.bodyJson = richDocFrom(args.bodyJson);
  message.bodyText = richDocToText(message.bodyJson);
  message.editedAt = cache.nowMs();
  refreshMentions(cache, 'message', String(message.id), message.bodyText);
  logActivity(cache, {
    mutator: 'message.edit',
    entityTable: 'message',
    entityId: String(message.id),
    op: 'update',
    before,
    after: { bodyText: message.bodyText, editedAt: message.editedAt },
  });
  fanoutLast(cache);
  const keys = [`channel:${String(message.channelId)}`];
  if (message.threadRootId !== null) {
    keys.push(`thread:${String(message.threadRootId)}`);
  }
  bump(...keys);
}

export function softDeleteMessage(args: { id: MessageId }): void {
  const cache = requireCache();
  const message = cache.messages.get(args.id);
  if (message === undefined) {
    return;
  }
  const before = { deletedAt: message.deletedAt };
  message.deletedAt = cache.nowMs();
  logActivity(cache, {
    mutator: 'message.softDelete',
    entityTable: 'message',
    entityId: String(message.id),
    op: 'update',
    before,
    after: { deletedAt: message.deletedAt },
  });
  fanoutLast(cache);
  const keys = [`channel:${String(message.channelId)}`];
  if (message.threadRootId !== null) {
    keys.push(`thread:${String(message.threadRootId)}`);
  }
  bump(...keys);
}

export function toggleReaction(args: {
  id: ReactionId;
  ownerKind: 'message' | 'comment';
  ownerId: MessageId | CommentId;
  emoji: string;
}): void {
  const cache = requireCache();
  const normalized = normalizeEmoji(args.emoji);
  const ownerKey = `${args.ownerKind}:${String(args.ownerId)}`;
  let existingId: ReactionId | null = null;
  for (const id of cache.index.reactionsByOwner.get(ownerKey) ?? []) {
    const reaction = cache.reactions.get(id);
    if (reaction !== undefined && reaction.userId === cache.me && reaction.emoji === normalized) {
      existingId = reaction.id;
      break;
    }
  }
  if (existingId !== null) {
    const reaction = cache.reactions.get(existingId);
    if (reaction !== undefined) {
      removeFromIndex(cache, 'reaction', String(reaction.id));
      cache.reactions.delete(reaction.id);
      logActivity(cache, {
        mutator: 'reaction.toggle',
        entityTable: 'reaction',
        entityId: String(reaction.id),
        op: 'delete',
        before: snapshot(reaction),
        after: null,
      });
      fanoutLast(cache);
    }
  } else {
    const reaction = {
      id: args.id,
      ownerKind: args.ownerKind,
      ownerId: args.ownerId,
      userId: cache.me,
      emoji: normalized,
      createdAt: cache.nowMs(),
    };
    cache.reactions.set(reaction.id, reaction);
    addToIndex(cache, 'reaction', String(reaction.id));
    logActivity(cache, {
      mutator: 'reaction.toggle',
      entityTable: 'reaction',
      entityId: String(reaction.id),
      op: 'insert',
      before: null,
      after: snapshot(reaction),
    });
    fanoutLast(cache);
  }
  if (args.ownerKind === 'message') {
    const message = cache.messages.get(args.ownerId as MessageId);
    if (message !== undefined) {
      const keys = [`channel:${String(message.channelId)}`];
      if (message.threadRootId !== null) {
        keys.push(`thread:${String(message.threadRootId)}`);
      }
      bump(...keys);
    }
  } else {
    const comment = cache.comments.get(args.ownerId as CommentId);
    if (comment !== undefined) {
      bump(...taskKeys(cache, comment.taskId));
    }
  }
}

export function togglePin(args: { id: MessageId }): void {
  const cache = requireCache();
  const message = cache.messages.get(args.id);
  if (message === undefined) {
    throw new Error('Сообщение не найдено');
  }
  const before = { pinned: message.pinned };
  message.pinned = !message.pinned;
  logActivity(cache, {
    mutator: 'message.togglePin',
    entityTable: 'message',
    entityId: String(message.id),
    op: 'update',
    before,
    after: { pinned: message.pinned },
  });
  fanoutLast(cache);
  bump(`channel:${String(message.channelId)}`);
}

export function markRead(args: { channelId: ChannelId; seq: number }): void {
  const cache = requireCache();
  const key = `${String(args.channelId)}:${String(cache.me)}`;
  const member = cache.members.get(key);
  if (member === undefined) {
    throw new Error('Вы не состоите в канале');
  }
  const before = { lastReadSeq: member.lastReadSeq, unreadCount: member.unreadCount, mentionCount: member.mentionCount };
  member.lastReadSeq = Math.max(member.lastReadSeq, args.seq);
  member.unreadCount = 0;
  member.mentionCount = 0;
  logActivity(cache, {
    mutator: 'channel.markRead',
    entityTable: 'channelMember',
    entityId: key,
    op: 'update',
    before,
    after: { lastReadSeq: member.lastReadSeq, unreadCount: 0, mentionCount: 0 },
  });
  fanoutLast(cache);
  bump(`channel:${String(args.channelId)}`);
}

export function markUnread(args: { channelId: ChannelId; fromMessageId: MessageId }): void {
  const cache = requireCache();
  const key = `${String(args.channelId)}:${String(cache.me)}`;
  const member = cache.members.get(key);
  const from = cache.messages.get(args.fromMessageId);
  if (member === undefined || from === undefined) {
    throw new Error('Канал или сообщение не найдены');
  }
  const before = { lastReadSeq: member.lastReadSeq, unreadCount: member.unreadCount, mentionCount: member.mentionCount };
  member.lastReadSeq = from.seq - 1;
  let unread = 0;
  let mentions = 0;
  const me = cache.users.get(cache.me);
  const handle = me !== undefined ? `@${me.handle}` : null;
  for (const id of cache.index.messagesByChannel.get(args.channelId) ?? []) {
    const message = cache.messages.get(id);
    if (message === undefined || message.deletedAt !== null || message.seq <= member.lastReadSeq) {
      continue;
    }
    unread += 1;
    if (handle !== null && message.bodyText.includes(handle)) {
      mentions += 1;
    }
  }
  member.unreadCount = unread;
  member.mentionCount = mentions;
  logActivity(cache, {
    mutator: 'channel.markUnread',
    entityTable: 'channelMember',
    entityId: key,
    op: 'update',
    before,
    after: { lastReadSeq: member.lastReadSeq, unreadCount: unread, mentionCount: mentions },
  });
  fanoutLast(cache);
  bump(`channel:${String(args.channelId)}`);
}

export function toggleSavedMessage(args: { messageId: MessageId }): boolean {
  const cache = requireCache();
  if (!cache.messages.has(args.messageId)) {
    throw new Error('Сообщение не найдено');
  }
  const key = `${String(cache.me)}:${String(args.messageId)}`;
  const had = cache.savedMessages.has(key);
  if (had) {
    cache.savedMessages.delete(key);
  } else {
    cache.savedMessages.set(key, {
      userId: cache.me,
      messageId: args.messageId,
      savedAt: cache.nowMs(),
    });
  }
  bump('saved', 'channels');
  return !had;
}

export function setMessageDraft(args: {
  channelId: ChannelId;
  threadRootId?: MessageId | null;
  text: string;
}): void {
  const cache = requireCache();
  if (!cache.channels.has(args.channelId)) {
    throw new Error('Канал не найден');
  }
  const root = args.threadRootId ?? null;
  const key = root === null ? String(args.channelId) : `${String(args.channelId)}:${String(root)}`;
  const text = args.text.trim();
  if (text.length === 0) {
    if (!cache.drafts.delete(key)) return;
  } else {
    cache.drafts.set(key, {
      channelId: args.channelId,
      threadRootId: root,
      text,
      updatedAt: cache.nowMs(),
    });
  }
  bump('drafts', 'channels');
}
